import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { canShowMaisSidebar } from "../lib/screenAccess";
import { cn } from "../lib/utils";
import { useHeader } from "./Header/context/HeaderContext";
import { useAppNav, type AppNavSection } from "./Header/hooks/useAppNav";
import { isNavPathActive, NavRichLink } from "./ui/nav-rich-link";
import type { HeaderNavLink } from "./Header/components/headerNavMeta";
import type { RichNavGroup } from "./ui/navigation-menu-06";

const STORAGE_KEY = "db_sidebar_mais_expanded";
const TOGGLE_EVENT = "db-sidebar-mais-toggle";
const HOVER_CLOSE_MS = 180;

function sectionLinks(section: AppNavSection): HeaderNavLink[] {
  if (section.groups) return section.groups.flatMap((group) => group.items);
  return section.items ?? [];
}

const SidebarMais: React.FC = () => {
  const { user } = useAuth();
  const { isMobileMenuOpen, closeMobileMenu } = useHeader();
  const { shouldHideNavigation, directLinks, panelSections, pathname } =
    useAppNav();

  const showDesktop = canShowMaisSidebar(user) && !shouldHideNavigation;
  const showMobileDrawer = !shouldHideNavigation && isMobileMenuOpen;

  const [expanded, setExpanded] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(STORAGE_KEY) !== "0";
  });
  const [openSectionId, setOpenSectionId] = useState<string | null>(null);
  const desktopRef = useRef<HTMLElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  const hoverCloseTimer = useRef<number | null>(null);

  const openSection =
    panelSections.find((section) => section.id === openSectionId) ?? null;

  const clearHoverClose = useCallback(() => {
    if (hoverCloseTimer.current != null) {
      window.clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  }, []);

  const closeFlyout = useCallback(() => {
    clearHoverClose();
    setOpenSectionId(null);
  }, [clearHoverClose]);

  const scheduleCloseFlyout = useCallback(() => {
    clearHoverClose();
    hoverCloseTimer.current = window.setTimeout(() => {
      setOpenSectionId(null);
      hoverCloseTimer.current = null;
    }, HOVER_CLOSE_MS);
  }, [clearHoverClose]);

  const openFlyout = useCallback(
    (sectionId: string) => {
      clearHoverClose();
      setOpenSectionId(sectionId);
    },
    [clearHoverClose]
  );

  const toggleSection = useCallback((sectionId: string) => {
    setOpenSectionId((current) => (current === sectionId ? null : sectionId));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, expanded ? "1" : "0");
  }, [expanded]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const syncFromStorage = () => {
      setExpanded(window.localStorage.getItem(STORAGE_KEY) !== "0");
    };
    window.addEventListener("storage", syncFromStorage);
    window.addEventListener(TOGGLE_EVENT, syncFromStorage);
    return () => {
      window.removeEventListener("storage", syncFromStorage);
      window.removeEventListener(TOGGLE_EVENT, syncFromStorage);
    };
  }, []);

  useEffect(() => {
    closeFlyout();
    closeMobileMenu();
  }, [pathname, closeFlyout, closeMobileMenu]);

  useEffect(() => {
    if (!isMobileMenuOpen) closeFlyout();
  }, [isMobileMenuOpen, closeFlyout]);

  useEffect(() => {
    closeFlyout();
  }, [expanded, closeFlyout]);

  useEffect(() => {
    if (!openSectionId && !isMobileMenuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (openSectionId) {
        closeFlyout();
        return;
      }
      closeMobileMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openSectionId, isMobileMenuOpen, closeFlyout, closeMobileMenu]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!openSectionId || !expanded) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (desktopRef.current?.contains(target)) return;
      if (mobileRef.current?.contains(target)) return;
      closeFlyout();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [openSectionId, expanded, closeFlyout]);

  useEffect(
    () => () => {
      clearHoverClose();
    },
    [clearHoverClose]
  );

  const totalLinks = useMemo(
    () =>
      directLinks.length +
      panelSections.reduce((n, s) => n + sectionLinks(s).length, 0),
    [directLinks, panelSections]
  );

  if (!showDesktop && !showMobileDrawer) return null;
  if (totalLinks === 0) return null;

  const renderRail = (collapsed: boolean) => (
    <AppNavRail
      directLinks={directLinks}
      sections={panelSections}
      pathname={pathname}
      openSectionId={openSectionId}
      collapsed={collapsed}
      onToggleSection={toggleSection}
      onHoverSection={openFlyout}
      onLeaveSection={scheduleCloseFlyout}
      onNavigate={closeMobileMenu}
    />
  );

  const renderFlyout = (collapsed: boolean, hoverEnabled: boolean) =>
    openSection ? (
      <SectionFlyout
        section={openSection}
        pathname={pathname}
        collapsed={collapsed}
        onBack={closeFlyout}
        onMouseEnter={hoverEnabled ? clearHoverClose : undefined}
        onMouseLeave={hoverEnabled ? scheduleCloseFlyout : undefined}
        onNavigate={() => {
          closeFlyout();
          closeMobileMenu();
        }}
      />
    ) : null;

  return (
    <>
      {showDesktop ? (
        <aside
          ref={desktopRef}
          aria-hidden={!expanded}
          className={cn(
            "sticky top-14 z-20 hidden h-[calc(100vh-3.5rem)] shrink-0 transition-[width] duration-300 ease-out lg:block",
            expanded
              ? "w-60 overflow-visible border-r border-neutral-200/70 bg-gradient-to-b from-white via-neutral-50 to-neutral-100/90 shadow-[6px_0_28px_rgba(15,23,42,0.08),inset_-1px_0_0_rgba(255,255,255,0.9)]"
              : "pointer-events-none w-0 overflow-hidden"
          )}
        >
          {expanded ? (
            <div className="relative flex h-full w-60 min-w-0 flex-col overflow-visible">
              {renderRail(false)}
              {renderFlyout(false, true)}
            </div>
          ) : null}
        </aside>
      ) : null}

      {showMobileDrawer ? (
        <div ref={mobileRef} className="fixed inset-0 top-14 z-30 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-neutral-900/40 backdrop-blur-[1px]"
            aria-label="Fechar menu"
            onClick={closeMobileMenu}
          />
          <aside
            className="relative flex h-full w-[min(20rem,86vw)] flex-col bg-gradient-to-b from-white via-neutral-50 to-neutral-100 shadow-[12px_0_40px_rgba(15,23,42,0.28)] animate-in slide-in-from-left-4 duration-200"
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navegação"
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-200/80 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900">Menu</p>
                <p className="text-[11px] text-neutral-500">
                  Escolha uma tela para abrir
                </p>
              </div>
              <button
                type="button"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
                onClick={closeMobileMenu}
                aria-label="Fechar menu"
              >
                <X size={18} />
              </button>
            </div>
            <div className="relative flex min-h-0 flex-1 flex-col">
              {renderRail(false)}
              {renderFlyout(false, false)}
            </div>
          </aside>
        </div>
      ) : null}
    </>
  );
};

function AppNavRail({
  directLinks,
  sections,
  pathname,
  openSectionId,
  collapsed,
  onToggleSection,
  onHoverSection,
  onLeaveSection,
  onNavigate,
}: {
  directLinks: HeaderNavLink[];
  sections: AppNavSection[];
  pathname: string;
  openSectionId: string | null;
  collapsed: boolean;
  onToggleSection: (id: string) => void;
  onHoverSection: (id: string) => void;
  onLeaveSection: () => void;
  onNavigate: () => void;
}) {
  return (
    <nav
      className={cn(
        "min-h-0 flex-1 overflow-y-auto overscroll-contain py-3",
        collapsed ? "space-y-3 px-1.5" : "space-y-3 px-2.5"
      )}
    >
      {directLinks.length > 0 ? (
        <div className={cn(collapsed ? "space-y-1" : "space-y-0.5")}>
          {!collapsed ? (
            <p className="mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400">
              Principal
            </p>
          ) : null}
          {directLinks.map((item) => (
            <NavRichLink
              key={item.href}
              item={item}
              pathname={pathname}
              compact
              iconOnly={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ) : null}

      {sections.length > 0 ? (
        <div className={cn(collapsed ? "space-y-1" : "space-y-0.5")}>
          {!collapsed && directLinks.length > 0 ? (
            <p className="mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400">
              Módulos
            </p>
          ) : null}
          {sections.map((section) => {
            const links = sectionLinks(section);
            const isDirectLink = !section.groups && links.length === 1;

            if (isDirectLink) {
              const item = {
                ...links[0],
                title: section.title,
                icon: section.icon,
              };
              return (
                <NavRichLink
                  key={section.id}
                  item={item}
                  pathname={pathname}
                  compact
                  iconOnly={collapsed}
                  onNavigate={onNavigate}
                />
              );
            }

            const SectionIcon = section.icon;
            const isOpen = openSectionId === section.id;
            const isActive = links.some((item) =>
              isNavPathActive(pathname, item.href)
            );
            const highlighted = isOpen || isActive;

            return (
              <button
                key={section.id}
                type="button"
                title={collapsed ? section.title : undefined}
                aria-label={section.title}
                className={cn(
                  "group relative flex w-full text-left transition-all duration-200",
                  collapsed
                    ? "items-center justify-center rounded-xl p-1.5"
                    : "items-start gap-2.5 rounded-lg px-2.5 py-2 max-lg:items-center max-lg:py-2.5",
                  highlighted
                    ? collapsed
                      ? "bg-primary/[0.12] shadow-[inset_0_1px_0_rgba(255,255,255,0.55),0_6px_14px_rgba(139,0,0,0.12)]"
                      : "bg-primary/[0.08]"
                    : collapsed
                      ? "hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_8px_16px_rgba(15,23,42,0.1)]"
                      : "hover:bg-neutral-100/80"
                )}
                onClick={() => {
                  if (collapsed) {
                    onHoverSection(section.id);
                    return;
                  }
                  onToggleSection(section.id);
                }}
                onMouseEnter={() => {
                  if (collapsed) onHoverSection(section.id);
                }}
                aria-expanded={isOpen}
              >
                <span
                  className={cn(
                    "inline-flex shrink-0 items-center justify-center rounded-lg transition-colors",
                    collapsed ? "h-8 w-8" : "mt-0.5 h-7 w-7 rounded-md",
                    highlighted
                      ? "bg-primary/15 text-primary"
                      : "bg-primary/10 text-primary group-hover:bg-primary/15"
                  )}
                >
                  <SectionIcon
                    className={collapsed ? "h-4 w-4" : "h-3.5 w-3.5"}
                    strokeWidth={2}
                  />
                </span>
                {!collapsed ? (
                  <>
                    <span
                      className={cn(
                        "min-w-0 flex-1 break-words text-sm font-medium leading-snug tracking-tight",
                        highlighted ? "text-primary" : "text-neutral-800"
                      )}
                    >
                      {section.title}
                    </span>
                    <ChevronRight
                      size={15}
                      strokeWidth={2}
                      className={cn(
                        "mt-1 shrink-0 transition-transform duration-200 max-lg:mt-0",
                        highlighted
                          ? "translate-x-0.5 text-primary"
                          : "text-neutral-300 group-hover:text-neutral-400"
                      )}
                      aria-hidden
                    />
                  </>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </nav>
  );
}

function SectionFlyout({
  section,
  pathname,
  collapsed,
  onBack,
  onNavigate,
  onMouseEnter,
  onMouseLeave,
}: {
  section: AppNavSection;
  pathname: string;
  collapsed?: boolean;
  onBack: () => void;
  onNavigate: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const SectionIcon = section.icon;

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        "flex flex-col overflow-hidden bg-white",
        "max-lg:absolute max-lg:inset-0 max-lg:z-20",
        "lg:absolute lg:left-full lg:top-3 lg:z-30 lg:ml-1 lg:max-h-[calc(100vh-6rem)] lg:w-[min(40rem,calc(100vw-5rem))] lg:rounded-2xl lg:border lg:border-neutral-200/80",
        "shadow-[0_24px_48px_rgba(15,23,42,0.18),0_2px_0_rgba(255,255,255,0.8)_inset]",
        collapsed &&
          "lg:animate-in lg:fade-in-0 lg:zoom-in-95 lg:slide-in-from-left-2 lg:duration-200"
      )}
    >
      <div className="flex items-center gap-2.5 border-b border-neutral-100 bg-gradient-to-r from-primary/[0.08] via-primary/[0.03] to-transparent px-3 py-2.5">
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-600 hover:bg-white hover:shadow-sm lg:hidden"
          onClick={onBack}
          aria-label="Voltar"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]">
          <SectionIcon className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-neutral-900">
            {section.title}
          </h2>
          {section.description ? (
            <p className="truncate text-[11px] text-neutral-500">
              {section.description}
            </p>
          ) : null}
        </div>
      </div>

      <div className="overflow-y-auto overscroll-contain bg-neutral-50/50 p-3">
        {section.groups ? (
          <div
            className={cn(
              "grid gap-3",
              section.groups.length > 1
                ? "grid-cols-1 sm:grid-cols-2 sm:items-start [&>*]:min-w-0"
                : "grid-cols-1"
            )}
          >
            {section.groups.map((group) => (
              <SectionGroup
                key={group.title}
                group={group}
                pathname={pathname}
                compact
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ) : (
          <div
            className={cn(
              "grid gap-1",
              (section.items?.length ?? 0) > 4
                ? "grid-cols-1 sm:grid-cols-2 sm:items-start [&>*]:min-w-0"
                : "grid-cols-1"
            )}
          >
            {(section.items ?? []).map((item) => (
              <NavRichLink
                key={item.href}
                item={item}
                pathname={pathname}
                compact
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionGroup({
  group,
  pathname,
  compact,
  onNavigate,
}: {
  group: RichNavGroup;
  pathname: string;
  compact?: boolean;
  onNavigate: () => void;
}) {
  return (
    <div className="rounded-xl bg-white p-2 shadow-[0_8px_20px_rgba(15,23,42,0.05)] ring-1 ring-neutral-200/60">
      <h6 className="px-2.5 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400">
        {group.title}
      </h6>
      <div className="space-y-0.5">
        {group.items.map((item) => (
          <NavRichLink
            key={item.href}
            item={item}
            pathname={pathname}
            compact={compact}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
}

export default SidebarMais;
